import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import HeroCarousel from "@/components/HeroCarousel";
import ProductCard from "@/components/ProductCard";
import { Leaf, Truck, Shield, Sparkles } from "lucide-react";

const Section = ({ eyebrow, title, subtitle, children, seeAll }) => (
  <section className="py-12 lg:py-16" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <div className="flex items-end justify-between mb-8">
        <div>
          {eyebrow && <div className="text-xs tracking-[0.25em] uppercase text-[#8C6D46] mb-2">{eyebrow}</div>}
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1E2A24]">{title}</h2>
          {subtitle && <p className="text-[#64748B] mt-2 max-w-xl">{subtitle}</p>}
        </div>
        {seeAll && <Link to={seeAll} className="text-sm font-semibold text-[#C86D42] hover:underline">See All →</Link>}
      </div>
      {children}
    </div>
  </section>
);

const Home = () => {
  const [banners, setBanners] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [newArr, setNewArr] = useState([]);
  const [best, setBest] = useState([]);
  const [trending, setTrending] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [coupons, setCoupons] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/homepage-collections").then((r) => {
      setNewArr(r.data.new_arrivals || []);
      setBest(r.data.best_sellers || []);
    });
    api.get("/products/section/trending").then((r) => setTrending(r.data));
    api.get("/products/section/featured").then((r) => setFeatured(r.data));
    api.get("/coupons").then((r) => setCoupons(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-6">
        <HeroCarousel banners={banners} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Leaf, t: "100% Natural", s: "Chemical-free & clean" },
          { icon: Truck, t: "Free Shipping", s: "On orders above ₹499" },
          { icon: Shield, t: "Easy Returns", s: "Returns as per product policy" },
          { icon: Sparkles, t: "Handcrafted", s: "Made with tradition" },
        ].map((v) => (
          <div key={v.t} className="bg-white border border-[#E2DCD2] rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-[#EBF2EE] text-[#2D5A47]"><v.icon size={20} /></div>
            <div>
              <div className="text-sm font-semibold text-[#1E2A24]">{v.t}</div>
              <div className="text-xs text-[#64748B]">{v.s}</div>
            </div>
          </div>
        ))}
      </div>

      <Section eyebrow="Our Family" title="The Eira Family of Brands" subtitle="Three purposeful sub-brands under one roof — each with its own soul.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {brands.map((b) => (
            <Link key={b.id} to={`/brand/${b.slug}`} className="group relative rounded-xl overflow-hidden aspect-[4/3] card-lift" data-testid={`brand-tile-${b.slug}`} style={{ background: b.accent_color || "#2D5A47" }}>
              {b.image && (
                <img
                  src={b.image}
                  alt={b.name}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover opacity-50 transition-opacity group-hover:opacity-70"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D99B26] mb-2">An Eira Brand</div>
                <h3 className="font-serif text-3xl font-semibold">{b.name}</h3>
                <p className="text-sm text-white/80 mt-1">{b.tagline}</p>
                <div className="mt-3 text-xs font-semibold group-hover:translate-x-1 transition-transform">Explore {b.name} →</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section eyebrow="Explore" title="Shop by Category" subtitle="From homemade achars to handcrafted brass cookware — everyday essentials, thoughtfully curated.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {cats.map((c) => (
            <Link key={c.id} to={`/shop?category=${c.slug}`} className="group relative rounded-xl overflow-hidden aspect-[4/5] card-lift" data-testid={`cat-tile-${c.slug}`}>
              <img
                src={c.image}
                alt={c.name}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E2923]/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <div className="text-xs tracking-widest uppercase text-[#D99B26]">Eira</div>
                <h3 className="font-serif text-xl font-semibold">{c.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {featured.length > 0 && (
        <Section eyebrow="Curated" title="Featured Selection" subtitle="Our editor's picks this season." seeAll="/shop">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </Section>
      )}

      {best.length > 0 && (
        <Section eyebrow="Loved by all" title="Best Sellers" subtitle="Eira favourites that keep coming back." seeAll="/shop">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {best.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </Section>
      )}

      <section className="relative py-16 bg-[#2D5A47] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D99B26] mb-4">Diwali Kitchen Sale</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold leading-tight">Up to 40% off on Kitchen Classics</h2>
            <p className="mt-4 text-white/80 max-w-lg">Handcrafted brass, cast iron, terracotta and copper — timeless kitchen essentials at their best price of the year.</p>
            <Link to="/shop?category=kitchen" className="inline-block mt-6 bg-[#C86D42] hover:bg-[#AE5931] px-7 py-3 rounded-lg font-semibold">
              Shop Kitchen →
            </Link>
          </div>
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1610701596061-2ecf227e85b2?w=1200&q=80"
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {newArr.length > 0 && (
        <Section eyebrow="Just landed" title="New Arrivals" seeAll="/shop">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {newArr.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </Section>
      )}

      {trending.length > 0 && (
        <Section eyebrow="What's hot" title="Trending Now" seeAll="/shop">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {trending.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </Section>
      )}
    </div>
  );
};

export default Home;
